  <script setup>
    import { ref, computed, watchEffect, useTemplateRef, watch } from 'vue';
    import { useSpotStore } from "@/stores/SpotStore";
    import { useTidesStore } from "@/stores/TidesStore";
    import { useMediaQuery } from '@/composables/useMediaQuery';
    import { useUserStore } from '@/stores/UserStore'
    import TideSpotSelector from '@/components/TideSpotSelector.vue';
    import TideSpotEventGroupSolunar from '@/components/TideSpotEventGroupSolunar.vue';
    import TideSpotEventGroupEvents from '@/components/TideSpotEventGroupEvents.vue';
    import TideSpotEventGroupNow from '@/components/TideSpotEventGroupNow.vue';
    import TideGraph from '@/components/TideGraph.vue';
    import GraphCalendar from '@/components/GraphCalendar.vue';
    import TideCalendarWeekGraph from '@/components/TideCalendarWeekGraph.vue'; 
    import DateToolbar from '@/components/DateToolbar.vue'; 
    import ModalTideSpotSelection from "@/components/ModalTideSpotSelection.vue";
    import DataContainerLoader from '@/components/DataContainerLoader.vue';
    import { useDateHelper } from '@/composables/useDateHelper'
    import { addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, startOfDay, endOfDay, format } from 'date-fns';

    defineExpose({ getSettings });
    
    const emits = defineEmits(['calendarViewChanged']);

    const props = defineProps({
      settings: {
        type: Object,
        required: false
      },
      graphHeight: {
        type: Number,
        required: false,
      },
      containerSettings: {
        type: Object,
        required: false,
      },
      isFavorite: {
        type: Boolean,
        default: false
      },
      containerId: {
        type: Number,
        default: null,
      },
	  spotId: {
		type: Number,
		required: false
		}
    });  

    const graphCalendar = useTemplateRef('graph-calendar');

    const spotStore = useSpotStore(props.containerId);
    const tidesStore = useTidesStore(props.containerId);
    const userStore = useUserStore();

    let searchLat, searchLon;

    const spotTimezone = ref(null);
    
    if (props.isFavorite && props.containerSettings && Object.prototype.hasOwnProperty.call(props.containerSettings, 'tideSpotSearch')) {
      const tideSpotSearchLatLon = props.containerSettings.tideSpotSearch.split(':');

      searchLat = parseFloat(tideSpotSearchLatLon[0]);
      searchLon = parseFloat(tideSpotSearchLatLon[1]);

      if (!spotStore.spot) {
        await tidesStore.getNearbyTideSpotsByLatLon(searchLat, searchLon);
      }
    } else {
      searchLat = spotStore.lat;
      searchLon = spotStore.lon;
    }
    
    spotTimezone.value = tidesStore.tideSpotTimezone;

    const dateHelper = useDateHelper();
    const teleportJumpControls = useMediaQuery('(max-width: 650px)');

    await tidesStore.getNearbyTideSpotsByLatLon(searchLat, searchLon);

    const calendarView = ref('week'); // 'week' or 'month'
    const calendarDatePickerValue = ref(new Date());
    const eventsContainerHeight = ref(0);
    const startDate = ref(null);
    const endDate = ref(null);
    const showModal = ref(false);

    const showTideContent = computed(() => tidesStore.selectedNearbyTideSpot && tidesStore.tideData.length > 0);
    const showTideDataTables = computed(() => showTideContent.value && (!props.settings || props.settings[getContainerSetting('TidesShowTideEventTables')]));
    const showTideGraph = computed(() => showTideContent.value && (!props.settings || props.settings[getContainerSetting('TidesShowGraph')]));
    const showTideSpotNowEventGroup = computed(() => showTideContent.value && !tidesStore.nowTideValues.some(val => !val));
    const showGraphCalendar = computed(() => tidesStore.selectedNearbyTideSpot && (!props.settings || props.settings[getContainerSetting('TidesShowPreviewGraph')]));
    const selectedDate = computed(() => format(tidesStore.tideDate, 'MMMM dd, yyyy'));
    const eventsContainerIsTall = computed(() => parseInt(eventsContainerHeight.value) >= 180);
    const userUnits = computed(() => tidesStore.selectedNearbyTideSpot.type === 'currents' ? userStore.unitsWind : userStore.unitsHeight)

    const solunarEvents = computed(() => ({
      sunrise: tidesStore.sunrise,
      sunset: tidesStore.sunset,
      moonrise: tidesStore.moonrise,
      moonset: tidesStore.moonset,
      moonStatus: tidesStore.moonStatus
    }))

    function getContainerSetting(baseSettingName) {
      return Object.keys(props.settings).find(setting => setting.toLowerCase().indexOf(baseSettingName.toLowerCase()) > -1);
    }

    function getSettings(containerType) {
      return tidesStore.getSettings();
    }

    async function handleTideSpotUpdate(spotId) {
      tidesStore.setSelectedNearbyTideSpot(spotId);
    }

    function handleSelectedDateUpdate(date) {
      tidesStore.tideDate = date;

      if (graphCalendar.value) {
        graphCalendar.value.updateSelectedCalendarDate(date);
      }
    }

    function handleStartDateUpdate(date) {
      startDate.value = date;
    }

    function handleEndDateUpdate(date) {
      endDate.value = date;
    }

    // Date toolbar handlers
    function handleDatePickerChange(e) {
      const datePickerDate = new Date(`${e}T00:00:01`);
      
      if (graphCalendar.value) {
        graphCalendar.value.updateSelectedCalendarDate(datePickerDate);
      } else {
        tidesStore.tideDate = datePickerDate;
      }
    }

    function handlePreviousMonth() {
      if (!showGraphCalendar.value) {
        tidesStore.tideDate = subMonths(tidesStore.tideDate, 1);

        return;
      }

      graphCalendar.value.subtractMonth();
    }

    function handlePreviousWeek() {
      if (!showGraphCalendar.value) {
        tidesStore.tideDate = subWeeks(tidesStore.tideDate, 1);

        return;
      }
      
      graphCalendar.value.subtractWeek();
    }

    function handlePreviousDay() {
      if (!showGraphCalendar.value) {
        tidesStore.tideDate = subDays(tidesStore.tideDate, 1);

        return;
      }
      
      graphCalendar.value.subtractDay();
    }

    function handleToday() {
      if (!showGraphCalendar.value) {
        tidesStore.tideDate = new Date();

        return;
      }
      
      graphCalendar.value.today();
    }

    function handleNextDay() {
      if (!showGraphCalendar.value) {
        tidesStore.tideDate = addDays(tidesStore.tideDate, 1);

        return;
      }

      graphCalendar.value.addDay();
    }

    function handleNextWeek() {
      if (!showGraphCalendar.value) {
        tidesStore.tideDate = addWeeks(tidesStore.tideDate, 1);

        return;
      }

      graphCalendar.value.addWeek();
    }

    function handleNextMonth() {
      if (!showGraphCalendar.value) {
        tidesStore.tideDate = addMonths(tidesStore.tideDate, 1);

        return;
      }

      graphCalendar.value.addMonth();
    }

    function handleCalendarViewChange(event) {
      emits('calendarViewChanged', event);
    }

    function handleContainerHeightChange(height) {
      eventsContainerHeight.value = height;
    }

    watchEffect(() => {
      if (tidesStore.tideDate) {
        calendarDatePickerValue.value = format(new Date(tidesStore.tideDate), 'yyyy-MM-dd');
      }
    })

    async function getTideSpots(lat, lon) {
      await tidesStore.getNearbyTideSpotsByLatLon(lat, lon);
      tidesStore.setSelectedNearbyTideSpot(null, false);
    }
    
    // the spotId will change when the user has the map detail panel open
    // and while clicking on spot markers.
    watch(() => props.spotId, async (newSpotId) => {
      if (!newSpotId) return;
      
      await getTideSpots(spotStore.lat, spotStore.lon);
    })

    if (showTideContent.value && !showGraphCalendar.value) {
      watchEffect(async() => {

        if (!tidesStore.selectedNearbyTideSpot) return;

        startDate.value = dateHelper.adjustDateTimeFromUserTZToTZOffset(startOfDay(subDays(tidesStore.tideDate, 1)), tidesStore.tideSpotTimezoneOffset);
        endDate.value = dateHelper.adjustDateTimeFromUserTZToTZOffset(endOfDay(addDays(tidesStore.tideDate, 6)), tidesStore.tideSpotTimezoneOffset);

        await tidesStore.getAllTideDataByDateRange({
          tide_site_name: tidesStore.selectedNearbyTideSpot.location,
          begin_time: startDate.value,
          end_time: endDate.value
        });
      });
    }
  </script>

  <template>
    <DataContainerLoader v-if="!showTideContent" />
    <div
      v-if="showTideContent"
      class="jwx-data-container__toolbar"
      style="width: 100%;"
    >
      <TideSpotSelector
        :selectedTideSpot="tidesStore.selectedNearbyTideSpot"
        :hasCurrentsSpot="tidesStore.hasCurrentsSpot"
        @open:modalTideSpotSelector="showModal = true"
      />
    </div>
    <DateToolbar 
      v-if="showTideContent"
      v-model:selectedDate="selectedDate"
      v-model:datePickerValue="calendarDatePickerValue"
      :teleportJumpControls="teleportJumpControls"
      :teleportTarget="'.jwx-tide-spot__mobile-jump-controls'"
      @dateSelected="handleDatePickerChange"
      @previousMonth="handlePreviousMonth"
      @previousWeek="handlePreviousWeek"
      @previousDay="handlePreviousDay"
      @today="handleToday"
      @nextDay="handleNextDay"
      @nextWeek="handleNextWeek"
      @nextMonth="handleNextMonth"
    />
    <div
      v-if="showTideDataTables"
      ref="tide-events-container"
      class="jwx-tide-spot__event-groups"
      :data-has-now-event-group="showTideSpotNowEventGroup"
    >
      <TideSpotEventGroupNow
        v-if="showTideSpotNowEventGroup"
        :nextTideEvent="tidesStore.nextTideEvent"
        :nowTideValues="tidesStore.nowTideValues"
        :units="userUnits"
      />
      <TideSpotEventGroupEvents
        :tideEvents="tidesStore.tideEvents"
        @change:containerHeight="handleContainerHeightChange"
      />
      <TideSpotEventGroupSolunar
        :solunarEvents="solunarEvents"
        :isTall="eventsContainerIsTall"
      />
    </div>
    <div class="jwx-tide-spot__mobile-jump-controls"></div>
    <div
      v-if="showTideGraph"
      class="jwx-data-container__content"
      style="width: 100%;"
    >
      <TideGraph
        v-if="showTideGraph"
        :graphHeight="props.graphHeight"
        :tideData="tidesStore.tideData"
        :tideSpotType="tidesStore.selectedNearbyTideSpot.type"
        :tideSpotTimezone="spotTimezone"
        :tideSpotTimezoneOffset="tidesStore.tideSpotTimezoneOffset"
        :tideDataMax="tidesStore.tideDataMax"
        :tideEvents="tidesStore.tideEvents"
        :solunarEvents="tidesStore.solunarEvents"
        :tideUnits="userUnits"
      />
    </div>

    <GraphCalendar
      v-if=showGraphCalendar
      ref="graph-calendar"
      @update:selectedDate="handleSelectedDateUpdate"
      @update:startDate="handleStartDateUpdate"
      @update:endDate="handleEndDateUpdate"
      @changed:calendarView="handleCalendarViewChange"
      :calendarView="calendarView"
      :graphComponent="TideCalendarWeekGraph"
      :calendarViewChangeCallback="tidesStore.allDataCleanup"
      :dataReady="showTideContent"
      :storeId="props.containerId"
    />

    <ModalTideSpotSelection
      v-if="tidesStore.selectedNearbyTideSpot"
      v-model="showModal"
      :spot="spotStore.spot"
      :selectedTideSpot="tidesStore.selectedNearbyTideSpot"
      :nearbyTideSpots="tidesStore.nearbyTideSpots"
      :hasCurrentsSpot="tidesStore.hasCurrentsSpot"
      @selected:tideSpot="handleTideSpotUpdate"
    /> 
  </template>